import { useState, useEffect } from 'react';
import { X, Loader2, Download, Anchor, Ship, Activity, DollarSign, Image as ImageIcon, FileText, Edit2, Save, RotateCcw } from 'lucide-react';
import api from '../../../lib/api';
import { toast } from 'react-hot-toast';

export default function ServerReportModal({ report, onClose, onDownload, isDownloading, approveReport, isApproving, canAccess }) {
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({});

    useEffect(() => {
        const fetchDetails = async () => {
            if (!report?.job?.id) {
                toast.error('Job ID is missing');
                return;
            }

            setLoading(true);
            try {
                const res = await api.get(`/company/jobs/${report.job.id}/server-report`);

                if (res?.data?.success && res?.data?.data?.server_report) {
                    setDetails(res.data.data.server_report);
                    setFormData(res.data.data.server_report);
                } else {
                    toast.error('No report data found');
                    setDetails(null);
                }
            } catch (error) {
                console.error("Failed to fetch server report", error);
                toast.error(error.response?.data?.message || "Failed to load report");
                setDetails(null);
            } finally {
                setLoading(false);
            }
        };

        fetchDetails();
    }, [report]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        if (files && files[0]) {
            setFormData(prev => ({ ...prev, [name]: files[0] }));
        }
    };

    const handleNestedChange = (index, arrayName, field, value) => {
        const newArray = [...(formData[arrayName] || [])];
        newArray[index] = { ...newArray[index], [field]: value };
        setFormData(prev => ({ ...prev, [arrayName]: newArray }));
    };

    const handleSave = async () => {
        if (!report?.job?.id) {
            toast.error('Job ID is missing');
            return;
        }

        setIsSaving(true);
        try {
            // Use FormData for all requests to ensure consistency with the user's successful Postman tests
            const dataToSend = new FormData();
            dataToSend.append('_method', 'PUT');

            // Fields that should NOT be sent back to the API payload
            const excludedFields = [
                'id', 'job', 'created_at', 'updated_at', 'job_id',
                'job_server_report_id', 'boat_image', 'hin_image',
                'boat_image_url', 'hin_image_url', 'main_report'
            ];

            Object.keys(formData).forEach(key => {
                // Skip excluded fields and internal URL keys
                if (excludedFields.includes(key) || key.endsWith('_url')) return;

                const value = formData[key];

                // Handle arrays (engines, generators, notes) by appending each item/property specifically
                if (['engines', 'generators', 'notes'].includes(key) && Array.isArray(value)) {
                    value.forEach((item, index) => {
                        Object.keys(item).forEach(subKey => {
                            // Format: server_report[engines][0][model]
                            // Skip technical IDs and timestamps within nested items if necessary, 
                            // but usually sending them is fine or handled by server
                            if (['created_at', 'updated_at'].includes(subKey)) return;
                            dataToSend.append(`server_report[${key}][${index}][${subKey}]`, item[subKey] !== null && item[subKey] !== undefined ? item[subKey] : '');
                        });
                    });
                }
                // Handle files separately
                else if (value instanceof File) {
                    // Handled specifically below
                }
                // Send standard text fields wrapped in server_report[...]
                else if (typeof value !== 'object' || value === null) {
                    dataToSend.append(`server_report[${key}]`, value !== null && value !== undefined ? String(value) : '');
                }
            });

            // Specific handling for images to ensure they are wrapped correctly
            if (formData.boat_image instanceof File) {
                dataToSend.append('server_report[boat_image]', formData.boat_image);
            }
            if (formData.hin_image instanceof File) {
                dataToSend.append('server_report[hin_image]', formData.hin_image);
            }

            // Consistent endpoint and method usage as per working Postman example
            const res = await api.post(`/company/server-reports/${report.job.id}`, dataToSend, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (res.data.success) {
                toast.success("Report updated successfully");
                const updatedData = res.data.data?.server_report
                    || (Array.isArray(res.data.data) ? res.data.data[0] : res.data.data);

                setDetails(updatedData);
                setFormData(updatedData);
                setIsEditing(false);
            }
        } catch (error) {
            console.error("Update failed", error);
            const errorMsg = error.response?.data?.errors
                ? Object.values(error.response.data.errors).flat().join(', ')
                : error.response?.data?.message || "Failed to update report";
            toast.error(errorMsg);
        } finally {
            setIsSaving(false);
        }
    };

    const getImageUrl = (path) => {
        if (!path) return '';
        if (path instanceof File) return URL.createObjectURL(path);
        if (path.startsWith('http') || path.startsWith('blob:')) return path;

        // If the API URL ends with /api, remove it to get the base domain for storage
        // This fixes common issues where images are in root/storage but API is in /api
        const baseUrl = process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || '';

        // Ensure path starts with /
        const normalizedPath = path.startsWith('/') ? path : `/${path}`;

        return `${baseUrl}${normalizedPath}`;
    };

    const InfoRow = ({ label, name, value, type = "text" }) => (
        <div className="py-2 border-b border-gray-100 last:border-0">
            <span className="text-xs text-gray-500 block mb-1">{label}</span>
            {isEditing ? (
                <input
                    type={type}
                    name={name}
                    value={formData[name] || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500/20"
                />
            ) : (
                <span className="text-sm text-gray-900 font-medium">{value || 'N/A'}</span>
            )}
        </div>
    );

    const SectionHeader = ({ icon: Icon, title }) => (
        <h3 className="flex items-center gap-2 font-bold text-gray-900 mb-3 pb-2 border-b">
            <Icon className="w-4 h-4 text-emerald-600" />
            {title}
        </h3>
    );

    if (!report?.job?.id) {
        return (
            <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4" onClick={onClose}>
                <div className="bg-white rounded-xl p-6 max-w-md">
                    <p className="text-red-600">Invalid report data</p>
                    <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-200 rounded-lg">Close</button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[80] flex justify-center items-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b bg-gray-50 rounded-t-2xl">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            Server Report - Job #{report.job?.job_number}
                            {isEditing && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Editing</span>}
                        </h2>
                    </div>
                    <div className="flex items-center gap-2">
                        {!isEditing && details && (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm flex items-center gap-2 hover:bg-emerald-700"
                            >
                                <Edit2 className="w-4 h-4" />
                                Edit
                            </button>
                        )}
                        <button
                            onClick={onDownload}
                            disabled={isDownloading || isEditing}
                            className="px-3 py-1.5 border border-blue-200 bg-blue-50 text-blue-700 rounded-lg text-sm flex items-center gap-2 hover:bg-blue-100 disabled:opacity-50"
                        >
                            {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            PDF
                        </button>
                        <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg">
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex justify-center items-center py-20">
                            <Loader2 className="w-10 h-10 animate-spin text-emerald-600" />
                        </div>
                    ) : details ? (
                        <div className="space-y-4">

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white p-4 rounded-xl border">
                                    <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                                        <ImageIcon className="w-4 h-4" /> Boat Image
                                    </h4>
                                    {isEditing ? (
                                        <div className="space-y-2">
                                            {(formData.boat_image_url || formData.boat_image) && (
                                                <img
                                                    src={getImageUrl(formData.boat_image_url || formData.boat_image)}
                                                    alt="Boat"
                                                    className="w-full h-32 object-cover rounded-lg"
                                                />
                                            )}
                                            <input
                                                type="file"
                                                name="boat_image"
                                                accept="image/*"
                                                onChange={handleFileChange}
                                                className="w-full text-xs file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:bg-emerald-50 file:text-emerald-700"
                                            />
                                        </div>
                                    ) : (details.boat_image_url || details.boat_image) ? (
                                        <img
                                            src={getImageUrl(details.boat_image_url || details.boat_image)}
                                            alt="Boat"
                                            className="w-full h-32 object-cover rounded-lg"
                                        />
                                    ) : <p className="text-xs text-gray-400">No image</p>}
                                </div>

                                <div className="bg-white p-4 rounded-xl border">
                                    <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                                        <ImageIcon className="w-4 h-4" /> HIN Image
                                    </h4>
                                    {isEditing ? (
                                        <div className="space-y-2">
                                            {(formData.hin_image_url || formData.hin_image) && (
                                                <img
                                                    src={getImageUrl(formData.hin_image_url || formData.hin_image)}
                                                    alt="HIN"
                                                    className="w-full h-32 object-cover rounded-lg"
                                                />
                                            )}
                                            <input
                                                type="file"
                                                name="hin_image"
                                                accept="image/*"
                                                onChange={handleFileChange}
                                                className="w-full text-xs file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:bg-emerald-50 file:text-emerald-700"
                                            />
                                            <input
                                                name="hin_title"
                                                placeholder="HIN Title"
                                                value={formData.hin_title || ''}
                                                onChange={handleInputChange}
                                                className="w-full px-3 py-1.5 text-xs border rounded-md"
                                            />
                                            <textarea
                                                name="hin_description"
                                                placeholder="HIN Description"
                                                value={formData.hin_description || ''}
                                                onChange={handleInputChange}
                                                rows={2}
                                                className="w-full px-3 py-1.5 text-xs border rounded-md"
                                            />
                                        </div>
                                    ) : (details.hin_image_url || details.hin_image) ? (
                                        <>
                                            <img
                                                src={getImageUrl(details.hin_image_url || details.hin_image)}
                                                alt="HIN"
                                                className="w-full h-32 object-cover rounded-lg mb-2"
                                            />
                                            <div className="bg-gray-50 p-2 rounded text-xs">
                                                <p className="font-semibold">{details.hin_title || 'N/A'}</p>
                                                <p className="text-gray-600">{details.hin_description || 'N/A'}</p>
                                            </div>
                                        </>
                                    ) : <p className="text-xs text-gray-400">No HIN image</p>}
                                </div>
                            </div>

                            <div className="bg-white p-4 rounded-xl border">
                                <SectionHeader icon={Ship} title="Vessel Information" />
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <InfoRow label="Year Built" name="year_built" value={details.year_built} />
                                    <InfoRow label="Material" name="vessel_material" value={details.vessel_material} />
                                    <InfoRow label="Length Overall" name="length_overall" value={details.length_overall} />
                                    <InfoRow label="Beam" name="beam" value={details.beam} />
                                    <InfoRow label="Draft" name="draft" value={details.draft} />
                                    <InfoRow label="Displacement" name="displacement" value={details.displacement} />
                                    <InfoRow label="Hailing Port" name="document_hailing_port" value={details.document_hailing_port} />
                                    <InfoRow label="USCG Doc #" name="uscg_documentation_number" value={details.uscg_documentation_number} />
                                    <InfoRow label="Clarifications" name="vessel_clarifications_standard" value={details.vessel_clarifications_standard} />
                                </div>
                            </div>

                            {details.engines?.length > 0 && (
                                <div className="bg-white p-4 rounded-xl border">
                                    <SectionHeader icon={Activity} title="Engines" />
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {formData.engines?.map((eng, idx) => (
                                            <div key={idx} className="border rounded-lg p-3 space-y-2">
                                                <div className="font-bold text-emerald-700 text-sm">Engine #{eng.engine_number}</div>
                                                {isEditing ? (
                                                    <>
                                                        <input value={eng.model || ''} onChange={(e) => handleNestedChange(idx, 'engines', 'model', e.target.value)} placeholder="Model" className="w-full px-2 py-1 text-xs border rounded" />
                                                        <input value={eng.horsepower || ''} onChange={(e) => handleNestedChange(idx, 'engines', 'horsepower', e.target.value)} placeholder="HP" className="w-full px-2 py-1 text-xs border rounded" />
                                                        <input value={eng.hours || ''} onChange={(e) => handleNestedChange(idx, 'engines', 'hours', e.target.value)} placeholder="Hours" className="w-full px-2 py-1 text-xs border rounded" />
                                                        <input value={eng.serial_number || ''} onChange={(e) => handleNestedChange(idx, 'engines', 'serial_number', e.target.value)} placeholder="Serial" className="w-full px-2 py-1 text-xs border rounded" />
                                                    </>
                                                ) : (
                                                    <>
                                                        <p className="text-xs"><span className="text-gray-500">Model:</span> {eng.model || 'N/A'}</p>
                                                        <p className="text-xs"><span className="text-gray-500">HP:</span> {eng.horsepower || 'N/A'}</p>
                                                        <p className="text-xs"><span className="text-gray-500">Hours:</span> {eng.hours || 'N/A'}</p>
                                                        <p className="text-xs"><span className="text-gray-500">Serial:</span> {eng.serial_number || 'N/A'}</p>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-3 bg-yellow-50 p-3 rounded-lg border border-yellow-100">
                                        <p className="font-semibold text-yellow-800 text-xs mb-1">Engine Survey Notes</p>
                                        {isEditing ? (
                                            <textarea name="engine_mechanical_survey" value={formData.engine_mechanical_survey || ''} onChange={handleInputChange} rows={2} className="w-full px-3 py-2 text-xs border rounded-md" />
                                        ) : (
                                            <p className="text-xs">{details.engine_mechanical_survey || 'N/A'}</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {details.generators?.length > 0 && (
                                <div className="bg-white p-4 rounded-xl border">
                                    <SectionHeader icon={Activity} title="Generators" />
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {formData.generators?.map((gen, idx) => (
                                            <div key={idx} className="border rounded-lg p-3 space-y-2">
                                                <div className="font-bold text-blue-700 text-sm">Generator #{idx + 1}</div>
                                                {isEditing ? (
                                                    <>
                                                        <input value={gen.model || ''} onChange={(e) => handleNestedChange(idx, 'generators', 'model', e.target.value)} placeholder="Model" className="w-full px-2 py-1 text-xs border rounded" />
                                                        <input value={gen.power || ''} onChange={(e) => handleNestedChange(idx, 'generators', 'power', e.target.value)} placeholder="Power" className="w-full px-2 py-1 text-xs border rounded" />
                                                        <input value={gen.hours || ''} onChange={(e) => handleNestedChange(idx, 'generators', 'hours', e.target.value)} placeholder="Hours" className="w-full px-2 py-1 text-xs border rounded" />
                                                        <input value={gen.serial_number || ''} onChange={(e) => handleNestedChange(idx, 'generators', 'serial_number', e.target.value)} placeholder="Serial" className="w-full px-2 py-1 text-xs border rounded" />
                                                    </>
                                                ) : (
                                                    <>
                                                        <p className="text-xs"><span className="text-gray-500">Model:</span> {gen.model || 'N/A'}</p>
                                                        <p className="text-xs"><span className="text-gray-500">Power:</span> {gen.power || 'N/A'}</p>
                                                        <p className="text-xs"><span className="text-gray-500">Hours:</span> {gen.hours || 'N/A'}</p>
                                                        <p className="text-xs"><span className="text-gray-500">Serial:</span> {gen.serial_number || 'N/A'}</p>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="bg-white p-4 rounded-xl border">
                                <SectionHeader icon={DollarSign} title="Valuation & Rating" />
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="text-center p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                                        <p className="text-xs text-emerald-600 font-bold">Market Value</p>
                                        {isEditing ? (
                                            <input name="estimated_market_value" value={formData.estimated_market_value || ''} onChange={handleInputChange} className="w-full mt-1 px-3 py-1.5 text-center text-sm border rounded-lg" />
                                        ) : (
                                            <p className="text-lg font-bold text-emerald-900 mt-1">${details.estimated_market_value || 'N/A'}</p>
                                        )}
                                    </div>
                                    <div className="text-center p-3 bg-blue-50 rounded-xl border border-blue-100">
                                        <p className="text-xs text-blue-600 font-bold">Replacement Cost</p>
                                        {isEditing ? (
                                            <input name="estimated_replacement_cost" value={formData.estimated_replacement_cost || ''} onChange={handleInputChange} className="w-full mt-1 px-3 py-1.5 text-center text-sm border rounded-lg" />
                                        ) : (
                                            <p className="text-lg font-bold text-blue-900 mt-1">${details.estimated_replacement_cost || 'N/A'}</p>
                                        )}
                                    </div>
                                    <div className="text-center p-3 bg-purple-50 rounded-xl border border-purple-100">
                                        <p className="text-xs text-purple-600 font-bold">Overall Rating</p>
                                        {isEditing ? (
                                            <input name="vessel_overall_rating" value={formData.vessel_overall_rating || ''} onChange={handleInputChange} className="w-full mt-1 px-3 py-1.5 text-center text-sm border rounded-lg" />
                                        ) : (
                                            <p className="text-lg font-bold text-purple-900 mt-1">{details.vessel_overall_rating || 'N/A'}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white p-4 rounded-xl border">
                                <SectionHeader icon={Anchor} title="Survey Details" />
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                    <InfoRow label="Survey Location" name="location_of_survey_inspection" value={details.location_of_survey_inspection} />
                                    <InfoRow label="Bottom Inspection" name="location_of_bottom_inspection" value={details.location_of_bottom_inspection} />
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <h4 className="text-xs font-bold text-gray-800 mb-1">Electrical/Mechanical</h4>
                                        {isEditing ? (
                                            <textarea name="electrical_mechanical_survey" value={formData.electrical_mechanical_survey || ''} onChange={handleInputChange} rows={2} className="w-full px-3 py-2 text-xs border rounded-md" />
                                        ) : (
                                            <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded">{details.electrical_mechanical_survey || 'N/A'}</p>
                                        )}
                                    </div>
                                    <div>
                                        <h4 className="text-xs font-bold text-gray-800 mb-1">Rails & Rigging</h4>
                                        {isEditing ? (
                                            <textarea name="rails_rigging_survey" value={formData.rails_rigging_survey || ''} onChange={handleInputChange} rows={2} className="w-full px-3 py-2 text-xs border rounded-md" />
                                        ) : (
                                            <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded">{details.rails_rigging_survey || 'N/A'}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {details.notes?.length > 0 && (
                                <div className="bg-white p-4 rounded-xl border">
                                    <SectionHeader icon={FileText} title="Additional Notes" />
                                    <div className="space-y-2">
                                        {details.notes.map((note) => (
                                            <div key={note.id} className="p-3 bg-gray-50 rounded-lg border">
                                                <p className="font-bold text-sm">{note.title}</p>
                                                <p className="text-xs text-gray-600 mt-1">{note.description}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                        </div>
                    ) : (
                        <div className="text-center py-20 text-gray-500">
                            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                            <p>No report data available</p>
                        </div>
                    )}
                </div>

                <div className="p-4 border-t bg-gray-50 rounded-b-2xl flex justify-between items-center">
                    <div className="text-xs text-gray-500">
                        {details?.updated_at && `Updated: ${new Date(details.updated_at).toLocaleString()}`}
                    </div>
                    <div className="flex gap-2">
                        {isEditing ? (
                            <>
                                <button
                                    onClick={() => {
                                        setIsEditing(false);
                                        setFormData(details);
                                    }}
                                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg text-sm hover:bg-gray-300 flex items-center gap-2"
                                >
                                    <RotateCcw className="w-4 h-4" /> Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-70 flex items-center gap-2"
                                >
                                    {isSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save</>}
                                </button>
                            </>
                        ) : (
                            <>
                                {canAccess('approve_report') && (
                                    <>
                                        <button
                                            onClick={() => approveReport({ id: report.report_id, is_approved: true })}
                                            disabled={isApproving || report.status === 'approved'}
                                            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-50"
                                        >
                                            {isApproving ? 'Processing...' : 'Approve'}
                                        </button>
                                        <button
                                            onClick={() => approveReport({ id: report.report_id, is_approved: false })}
                                            disabled={isApproving || report.status === 'rejected'}
                                            className="px-4 py-2 border border-red-300 text-red-600 rounded-lg text-sm hover:bg-red-50 disabled:opacity-50"
                                        >
                                            Reject
                                        </button>
                                    </>
                                )}
                                <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300">
                                    Close
                                </button>
                            </>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
}
